import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDebounce, useAxios } from 'packages/core'
import { Autocomplete } from 'packages/eid-ui'
import optionComponents from './optionComponents'
import { useQuery } from '@tanstack/react-query'

const ENTER_KEY_CODE = 13

const buildUrlWithQueryParams = (baseUrl, params) => {
    const queryStringParts = []
    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
            queryStringParts.push(`${key}=${encodeURIComponent(params[key])}`)
        }
    })
    const queryString = queryStringParts.join('&')
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

const useData = (url, data, enabled, method) => {
    const callApi = useAxios()
    const isGetMethod = method?.toUpperCase() === 'GET'
    const finalUrl = isGetMethod ? buildUrlWithQueryParams(url, data) : url

    return useQuery(
        ['SEARCH', url, ...Object.values(data)],
        () =>
            callApi({
                url: finalUrl,
                method: method,
                ...(isGetMethod ? {} : { data }),
            }).then((data) => data.data),
        {
            enabled,
        },
    )
}

const EidAutocomplete = ({
    url,
    queryParams = {},
    dedupingInterval = 2000,
    debounceInterval = 500,
    onChange,
    shouldTriggerApiCall = true,
    supportsServerSideSearch = true,
    advancedSearchColumns = [],
    minCharactersReqForSearch = 3,
    excludeOptions = [],
    clearFilterOption = {},
    method = 'POST',
    getOptionLabel,
    selectFirstByDefault = false,
    ...rest
}) => {
    const [callEndpoint, setCallEndpoint] = useState(false)
    const [options, setOptions] = useState([])

    const [searchKey, setSearchKey] = useState('')
    const debouncedValue = useDebounce(searchKey, debounceInterval)

    const apiData = { ...queryParams }
    if (supportsServerSideSearch && debouncedValue) {
        apiData.searchTerm = debouncedValue
    }

    if (advancedSearchColumns.length > 0 && debouncedValue) {
        apiData.advancedSearch = advancedSearchColumns.map((asc) => ({
            name: asc.name,
            type: asc.type,
            value: debouncedValue,
        }))
    }

    const { data, isFetching: isLoading } = useData(
        url,
        apiData,
        (callEndpoint || selectFirstByDefault) &&
            shouldTriggerApiCall &&
            Boolean(url),
        method,
    )

    useEffect(() => {
        if (minCharactersReqForSearch > 0 && Boolean(searchKey)) {
            setCallEndpoint(false)
            setOptions([])
        }
    }, [searchKey])

    useEffect(() => {
        if (data) {
            let options = data

            if (excludeOptions.length > 0) {
                options = data.filter((d) => !excludeOptions.includes(d.id))
            }
            if (clearFilterOption && clearFilterOption.value) {
                options = [clearFilterOption, ...options]
            }
            setOptions(options)
        }
    }, [data])

    useEffect(() => {
        if (
            minCharactersReqForSearch > 0 &&
            Boolean(debouncedValue) &&
            debouncedValue.length >= minCharactersReqForSearch
        ) {
            setCallEndpoint(true)
        }
    }, [minCharactersReqForSearch, debouncedValue])

    // Auto-select first option when data loads and selectFirstByDefault is true
    useEffect(() => {
        if (
            data &&
            selectFirstByDefault &&
            data.length > 0 &&
            !rest.value &&
            onChange
        ) {
            // Only auto-select if no value is currently set
            onChange(null, data[0])
        }
    }, [data, selectFirstByDefault, rest.value, onChange, url])

    return (
        <Autocomplete
            options={
                supportsServerSideSearch
                    ? options
                    : options.filter((option) => {
                          return getOptionLabel(option)
                              .toLowerCase()
                              .includes(debouncedValue.toLowerCase())
                      })
            }
            loading={
                (callEndpoint && shouldTriggerApiCall && !data) || isLoading
            }
            onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                    setSearchKey(value)
                }
            }}
            onKeyDown={(e) => {
                if (e.keyCode === ENTER_KEY_CODE) {
                    setCallEndpoint(true)
                }
            }}
            onChange={onChange}
            onOpen={() => {
                if (
                    !Boolean(debouncedValue) ||
                    (minCharactersReqForSearch > 0 &&
                        Boolean(debouncedValue) &&
                        debouncedValue.length >= minCharactersReqForSearch)
                ) {
                    setCallEndpoint(true)
                }
                if (!supportsServerSideSearch) {
                    setSearchKey('')
                }
            }}
            onClose={() => setCallEndpoint(false)}
            getOptionLabel={getOptionLabel}
            {...rest}
        />
    )
}

EidAutocomplete.getOptionComponent = ({ type, ...rest }) => {
    const OptionComponent = optionComponents[type]

    return ({ option, optionState }) => (
        <OptionComponent option={option} optionState={optionState} {...rest} />
    )
}

EidAutocomplete.propTypes = {
    /**
     * URL where to fetch the data from.
     */
    url: PropTypes.string,

    /**
     * Callback when there is a change in the value.
     */
    onChange: PropTypes.func,

    /**
     * Request deduplication interval (ms) for requests with the same key.
     * Default is set to 2000.
     */
    dedupingInterval: PropTypes.number,

    /**
     * Request debounce interval (ms).
     * Default is set to 300.
     */
    debounceInterval: PropTypes.number,

    /**
     * If server-side search is supported or not.
     */
    supportsSearch: PropTypes.bool,

    /**
     * Whether to automatically select the first option when data loads.
     * Only works when no value is currently set.
     */
    selectFirstByDefault: PropTypes.bool,
}

export default EidAutocomplete
